import { domains } from 'config/env'
import { buildClient } from './secondStepApi.shared'
import {
  adaptLegacySitePreferencesToV4,
  adaptLegacySiteUserPreferencesToV4,
} from 'lib/api/adapters/preferences'

const UNSUPPORTED_IN_V4_ERROR = methodName => {
  const message = `[secondStepApi.v4] ${methodName} is unsupported in v4`
  // eslint-disable-next-line no-console
  console.error(message)
  const error = new Error(message)
  error.name = 'UnsupportedEndpointError'
  throw error
}

export function makeClient(baseApiAddress) {
  return buildClient({
    baseApiAddress,
    extend: (shared, _api) => ({
      ...shared,
      // v4-only endpoints (not in shared)
      getNetSuiteAssetsForUser: async () => _api.get(`netsuiteassets/me`),
      getUserContext: async () => _api.get(`context/me`),
      // v4 endpoints migrated from shared (now v4-only)
      getUserAccess: async () => _api.get('access/me'),
      getUserSites: async () => _api.get('sites/me'),
      getValidatePak: async pakNumber => {
        const body = {
          pakNumber,
        }
        return _api.post(`pak/validate`, body)
      },
      getValidateAccessCode: accessCode => {
        const body = {
          accessCode,
        }
        return _api.post(`access/codes`, body)
      },
      postAccessCode: accessCode => {
        const body = {
          accessCode,
          processCode: true,
        }
        return _api.post(`access/codes`, body)
      },
      postPakNumber: async pakNumber => {
        const body = {
          pakNumber,
        }
        return _api.post(`userpak/me`, body)
      },
      updateSitePacingDates: async (
        siteId,
        programAccessCode,
        pacingUpdate,
      ) => {
        return _api.post(
          `sites/${siteId}/pacing/${programAccessCode}`,
          pacingUpdate,
        )
      },
      updateMspOptInSettings: async mspOptInArr => {
        return _api.patch(`sites/preferences`, mspOptInArr)
      },
      // v4 overrides (different implementation than v3)
      getSetupLicensesForUser: async () =>
        UNSUPPORTED_IN_V4_ERROR('getSetupLicensesForUser'),
      // In v4, getUserLicenses maps to netsuiteassets/me but logs a warning
      getUserLicenses: async () => {
        if (process.env.NODE_ENV !== 'production') {
          // eslint-disable-next-line no-console
          console.warn(
            '[secondStepApi.v4] getUserLicenses: endpoint removed; proxying to netsuiteassets/me',
          )
        }
        return _api.get(`netsuiteassets/me`)
      },
      // v4: preferences endpoints (with adapters)
      updateSitePreferences: async (
        siteId,
        legacyPreferences,
        { referenceSites } = {},
      ) => {
        const payload = adaptLegacySitePreferencesToV4(legacyPreferences, {
          referenceSites,
        })
        return _api.put(`sites/${siteId}/preferences`, payload)
      },
      updateSiteUserPreferences: async (
        siteId,
        legacyPreferences,
        { referenceSites } = {},
      ) => {
        const payload = adaptLegacySiteUserPreferencesToV4(legacyPreferences, {
          referenceSites,
        })
        // this uses the same endpoint as updateSitePreferences but with SiteUser preferences (previously on v3 these were two different endpoints)
        return _api.put(`sites/${siteId}/preferences`, payload)
      },
    }),
  })
}
const baseApiAddress = `${domains.SECONDSTEP_API}/v4/`
const api = makeClient(baseApiAddress)

export default api
